import React, { useEffect, useState } from 'react';
import {
    Box,
    Link,
    Checkbox,
    Button,
    Backdrop,
    CircularProgress,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogContentText,
    IconButton,
    Typography,
    FormControlLabel,
    Divider,
} from '@mui/material';
import { Formik, validateYupSchema, yupToFormErrors } from 'formik';
import { HelpOutline as QuestionIcon } from '@mui/icons-material';
import AddressMenu from '../components/registration/Address';
import LoginMenu from '../components/registration/Login';
import RoleMenu from '../components/registration/Role';
import UserMenu from '../components/registration/User';
import { genders, races, roles } from '../data';
import { validationSchema } from '../validators';
import { connect } from 'react-redux';
import { setUser, registerUserAndLogin } from '../store/actions/user';
import { setRole } from '../store/actions/role';
import { setAddress } from '../store/actions/address';
import { setLogin } from '../store/actions/auth';
import CusttomSnackbar from '../components/CusttomSnackbar.jsx';
import Transition from '../components/Transition.jsx';

const sleep = (milliseconds) => (new Promise(resolve => setTimeout(resolve, milliseconds)));

const Registration = ({ addUser, addRole, addAddress, addLogin, register, errors, loading }) => {
    const [modal, setmodal] = useState(false);
    const [checked, setChecked] = useState(false);
    const [openError, setOpenError] = useState(false);
    const [openSave, setOpenSave] = useState(false);
    const [submitted, setSubmitted] = useState(false);

    const handleChangeSnackbar = (prop) => {
        prop === 'error' ? setOpenError(!openError) : setOpenSave(!openSave);
    };

    const handleModal = () => {
        setmodal(!modal);
    };

    const initialValues = {
        firstName: '',
        middleName: '',
        lastName: '',
        gender: genders[0],
        race: races[0],
        role: roles[0].value,
        organization: '',
        department: '',
        medicalId: '',
        site: '',
        group: '',
        country: null,
        state: null,
        city: null,
        street: 'DISABLED',
        zipcode: 0,
        email: '',
        password: ''
    };

    useEffect(() => {
        if (submitted && !loading && errors.length === 0) {
            setOpenSave(true);
            setChecked(false);
            sleep(1000).then(() => {
                window.location.href = '/';
            });
        }
    }, [errors, loading, submitted]);

    const SectionHeader = ({ children }) => (
        <Box sx={{ mb: 3, mt: 1 }}>
            <Typography
                variant="h6"
                sx={{
                    fontWeight: 600,
                    color: 'primary.main',
                    fontSize: '1.1rem',
                }}
            >
                {children}
            </Typography>
            <Divider sx={{ mt: 1 }} />
        </Box>
    );

    return (
        <Box id="registration-id">
            <Typography
                variant="h3"
                sx={{
                    fontWeight: 600,
                    color: 'text.primary',
                    mb: 2,
                    fontSize: { xs: '1.75rem', sm: '2.25rem' },
                }}
            >
                Create Account
            </Typography>
            <Typography
                variant="body1"
                sx={{
                    color: 'text.secondary',
                    mb: 4,
                    fontSize: '1rem',
                    lineHeight: 1.6,
                }}
            >
                Fill in your information to get started with NeuroLeap
            </Typography>

            <Formik
                enableReinitialize={false}
                initialValues={initialValues}
                validate={(values) => {
                    try {
                        validateYupSchema(values, validationSchema, true, values);
                    } catch (err) {
                        return yupToFormErrors(err);
                    }
                    return {};
                }}
                onSubmit={(values) => {
                    const getValues = (propsName = []) => propsName
                        .map(x => ({ [x === "medicalId" ? x : x.toLowerCase()]: values[x] }))
                        .reduce((a, b) => Object.assign(a, b), {});
                    addUser(getValues(['firstName', 'middleName', 'lastName', 'email', 'gender', 'race']));
                    addRole(getValues(['role', 'organization', 'department', 'medicalId', 'site', 'group']));
                    addAddress(getValues(['country', 'state', 'city', 'street', 'zipcode']));
                    addLogin(getValues(['email', 'password']));
                    register();
                    setSubmitted(true);
                    handleChangeSnackbar('error');
                }}
            >
                {({ handleSubmit }) => (
                    <form onSubmit={handleSubmit} autoComplete="off">
                        <Box sx={{ mb: 4 }}>
                            <SectionHeader>Personal Information</SectionHeader>
                            <UserMenu />
                        </Box>

                        <Box sx={{ mb: 4 }}>
                            <SectionHeader>Work Details</SectionHeader>
                            <RoleMenu />
                        </Box>

                        <Box sx={{ mb: 4 }}>
                            <SectionHeader>Location</SectionHeader>
                            <AddressMenu />
                        </Box>

                        <Box sx={{ mb: 4 }}>
                            <SectionHeader>Account Credentials</SectionHeader>
                            <LoginMenu />
                        </Box>

                        <Box sx={{ mt: 4, mb: 3 }}>
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={checked}
                                        onChange={(event) => setChecked(event.target.checked)}
                                        color="primary"
                                        sx={{ '& .MuiSvgIcon-root': { fontSize: 24 } }}
                                    />
                                }
                                label={
                                    <Typography variant="body1" sx={{ fontSize: '0.95rem' }}>
                                        I accept the{' '}
                                        <Link
                                            href="/Terms_and_conditions_NLC.pdf"
                                            target="_blank"
                                            sx={{
                                                color: 'primary.main',
                                                textDecoration: 'none',
                                                '&:hover': { textDecoration: 'underline' },
                                            }}
                                        >
                                            Terms and Conditions
                                        </Link>
                                    </Typography>
                                }
                            />
                        </Box>

                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                            <Button
                                type="submit"
                                disabled={!checked}
                                variant="contained"
                                color="primary"
                                size="large"
                                sx={{
                                    py: 1.75,
                                    px: 5,
                                    borderRadius: 2,
                                    textTransform: 'none',
                                    fontSize: '1.1rem',
                                    fontWeight: 600,
                                }}
                            >
                                Create Account
                            </Button>
                            <IconButton
                                onClick={handleModal}
                                sx={{
                                    color: 'text.secondary',
                                    '&:hover': { color: 'primary.main' },
                                }}
                            >
                                <QuestionIcon />
                            </IconButton>
                        </Box>
                    </form>
                )}
            </Formik>

            <Backdrop
                sx={{
                    color: '#fff',
                    zIndex: (theme) => theme.zIndex.drawer + 1,
                }}
                open={loading}
            >
                <CircularProgress color="inherit" />
            </Backdrop>

            {(!loading && errors.length > 0) && (
                <CusttomSnackbar
                    message={errors[0]}
                    type="error"
                    open={openError}
                    handleChange={handleChangeSnackbar}
                />
            )}

            <CusttomSnackbar
                message="Successfully registered user"
                type="success"
                open={openSave}
                handleChange={handleChangeSnackbar}
            />

            <Dialog
                open={modal}
                TransitionComponent={Transition}
                aria-labelledby="title"
                aria-describedby="description"
                onClose={handleModal}
            >
                <DialogTitle id="title">Information</DialogTitle>
                <DialogContent>
                    <DialogContentText id="description">
                        You must accept the terms and conditions before registering.
                    </DialogContentText>
                </DialogContent>
            </Dialog>
        </Box>
    );
};

const mapStateToProps = state => {
    const { role, user, address } = state;
    return {
        loading: role.loading || user.loading || address.loading,
        errors: [role.error, user.error, address.error].filter(x => Boolean(x))
    };
};

const mapDispatchToProps = dispatch => ({
    addAddress: address => dispatch(setAddress(address)),
    addRole: role => dispatch(setRole(role)),
    addLogin: login => dispatch(setLogin(login)),
    addUser: user => dispatch(setUser(user)),
    register: () => dispatch(registerUserAndLogin())
});

export default connect(mapStateToProps, mapDispatchToProps)(Registration);
